import React, { useCallback, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { isEmpty } from 'lodash';
import Carousel from 'react-elastic-carousel';

// Redux
import { setOrderDataAction } from '../../redux/Actions/CreateOrderActions';
import { getPickupTimeApi } from '../../redux/Reducers/CreateOrderReducer';
import { getAllProvidersApi } from '../../redux/Reducers/ProvidersReducer';

// Utils
import { dateFn, utcTimeToLocalTime, getTimeZoneAbbr } from '../../utils/dateTimeFormatter';
import { formatAndSortStartTimes } from '../../utils/commonUtil';
import { generateInitials } from '../../components/Utils';

// Components
import Loader from '../../components/Loader';

// Images
import no_picture from '../../images/no-profile-picture.png';

export default function StepProvider({ onContinue }) {
  const dispatch = useDispatch();
  const { orderProvider, orderServiceAddress, selectedServices, orderType, orderTimeSlots } = useSelector(
    (s) => s.createNewOrder
  );
  const { orderPreferences } = useSelector((s) => s.myaccount);
  const [providersLoading, setProvidersLoading] = useState(false);
  const [providers, setProviders] = useState([]);
  const [autoMatchSchedules, setAutoMatchSchedules] = useState([]);
  const [selectedDate, setSelectedDate] = useState(null);

  const convertSchedules = useCallback(
    (schedules) => {
      return Object.entries(schedules || {}).map(([key, val]) => ({
        dateUnformatted: key,
        dateWeekday: dateFn('type7', key),
        dateMonthDate: dateFn('type8', key),
        times: val?.map((item) => ({
          start_time: utcTimeToLocalTime(`${key}T${item?.start_time}`, orderServiceAddress?.time_zone),
          end_time: utcTimeToLocalTime(`${key}T${item?.end_time}`, orderServiceAddress?.time_zone),
          default: { start_time: item?.start_time, end_time: item?.end_time },
          time_zone: getTimeZoneAbbr(`${key}T${item?.start_time}`, orderServiceAddress?.time_zone),
        })),
      }));
    },
    [orderServiceAddress?.time_zone]
  );

  useEffect(() => {
    setProvidersLoading(true);
    const svcIds = { service_ids: selectedServices?.map((s) => s.service_id) || [] };
    const type = orderType === 'oneTime' ? 'ORDER' : 'subscription';

    Promise.all([
      getAllProvidersApi(orderServiceAddress?.id, svcIds, type),
      getPickupTimeApi(null, orderServiceAddress?.id, type),
    ]).then(([provRes, timeRes]) => {
      setProvidersLoading(false);
      let provList = [];
      if (provRes?.status === 200 || provRes?.status === 201) provList = provRes?.data?.data || [];
      const provWithSchedules = provList.map((p) => ({
        ...p,
        schedules: convertSchedules(formatAndSortStartTimes(p?.schedules, orderServiceAddress?.time_zone) || {}),
        isPreferred: p?.id === orderPreferences?.preferred_worker?.id,
      }));
      // sort preferred first
      provWithSchedules.sort((a, b) => (b.isPreferred ? 1 : 0) - (a.isPreferred ? 1 : 0));
      setProviders(provWithSchedules);

      if (timeRes?.status === 200 || timeRes?.status === 201) {
        const sorted = formatAndSortStartTimes(timeRes?.data || {}, orderServiceAddress?.time_zone);
        setAutoMatchSchedules(convertSchedules(sorted || {}));
      }
    });
  }, [orderServiceAddress?.id, selectedServices]);

  const selectProvider = (prov, date, time) => {
    const provData = prov.id
      ? { first_name: prov.first_name, last_name: prov.last_name, id: prov.id, profile_image: prov.profile_image }
      : { id: null, first_name: 'Auto Match' };
    dispatch(
      setOrderDataAction({
        orderProvider: provData,
        orderTimeSlots: {
          ...(orderType === 'oneTime' ? { pickup_date: date } : { first_pickup_date: date }),
          pickup_start_time: time?.default?.start_time,
          pickup_end_time: time?.default?.end_time,
          timeZone: time?.time_zone,
        },
      })
    );
    setSelectedDate(date);
  };

  const isProvSelected = (prov) => {
    if (prov.id === null || prov.id === undefined) return orderProvider?.id === null;
    return orderProvider?.id === prov.id;
  };

  const tz = autoMatchSchedules?.[0]?.times?.[0]?.time_zone || '';
  const hasValid = !!orderTimeSlots?.pickup_start_time;

  const renderProviderCard = (prov, isAutoMatch = false) => {
    const schedules = isAutoMatch ? autoMatchSchedules : prov.schedules;
    const selected = isProvSelected(prov);
    const currentDate = selected
      ? orderTimeSlots?.pickup_date || orderTimeSlots?.first_pickup_date
      : schedules?.[0]?.dateUnformatted;

    return (
      <div
        key={isAutoMatch ? 'auto' : prov.id}
        className={`rounded-lg border-2 p-3 transition ${
          selected ? 'border-client-300 bg-blue-50/30' : 'border-slate-200 bg-white hover:border-slate-300'
        }`}
      >
        {/* Provider info */}
        <div
          className='flex cursor-pointer items-center gap-2'
          onClick={() => {
            const firstSch = schedules?.[0];
            selectProvider(isAutoMatch ? { id: null } : prov, firstSch?.dateUnformatted, firstSch?.times?.[0]);
          }}
        >
          <input type='radio' checked={selected} onChange={() => {}} className='text-client-300 h-4 w-4 shrink-0' />
          {isAutoMatch ? (
            <img className='h-10 w-10 rounded-full bg-slate-100' src={no_picture} alt='' />
          ) : prov.profile_image?.absolute_url ? (
            <img className='h-10 w-10 rounded-full object-cover' src={prov.profile_image.absolute_url} alt='' />
          ) : (
            <div className='flex h-10 w-10 items-center justify-center rounded-full bg-pink-400 text-sm font-bold text-white'>
              {generateInitials(`${prov.first_name} ${prov.last_name}`)}
            </div>
          )}
          <div className='min-w-0 flex-1'>
            <p className='text-sm font-semibold text-slate-800'>
              {isAutoMatch ? 'Auto-Match Me' : `${prov.first_name} ${prov.last_name?.charAt(0)}.`}
            </p>
            {isAutoMatch ? (
              <p className='text-xs text-slate-500'>We'll match you with an available provider</p>
            ) : (
              <p className='text-xs text-slate-500'>
                {prov.total_orders_delivered || 0} orders · {prov.rating || '–'} ★
              </p>
            )}
          </div>
        </div>
        {/* Schedule picker – only shown when selected */}
        {selected && !isEmpty(schedules) && (
          <div className='mt-3 border-t border-dashed border-slate-200 pt-3'>
            <p className='mb-2 text-center text-xs font-semibold text-slate-600'>
              Pickup Date & Time {tz && `(${tz})`}
            </p>
            {/* Dates carousel */}
            <div className='mb-2'>
              <Carousel itemsToScroll={1} itemsToShow={Math.min(schedules.length, 4)} pagination={false}>
                {schedules.map((s, idx) => (
                  <div
                    key={idx}
                    className={`mx-0.5 cursor-pointer rounded-lg border-2 px-2 py-1 text-center text-xs font-medium transition ${
                      currentDate === s.dateUnformatted
                        ? 'border-client-300 bg-blue-50'
                        : 'border-slate-100 bg-slate-50 hover:border-slate-300'
                    }`}
                    onClick={() => selectProvider(isAutoMatch ? { id: null } : prov, s.dateUnformatted, s.times?.[0])}
                  >
                    <span className='block'>{s.dateWeekday}</span>
                    <span>{s.dateMonthDate}</span>
                  </div>
                ))}
              </Carousel>
            </div>
            {/* Time slots */}
            <div className='flex flex-wrap justify-center gap-1.5'>
              {schedules
                .find((s) => s.dateUnformatted === currentDate)
                ?.times?.map((t, i) => {
                  const isSel =
                    selected &&
                    orderTimeSlots?.pickup_start_time === t.default.start_time &&
                    orderTimeSlots?.pickup_end_time === t.default.end_time &&
                    currentDate === (orderTimeSlots?.pickup_date || orderTimeSlots?.first_pickup_date);
                  return (
                    <button
                      key={i}
                      type='button'
                      className={`rounded-lg border-2 px-3 py-1.5 text-xs font-medium transition ${
                        isSel ? 'border-client-300 bg-blue-50' : 'border-slate-100 bg-slate-50 hover:border-slate-300'
                      }`}
                      onClick={() => {
                        const provData = isAutoMatch ? { id: null } : prov;
                        const provInfo = provData.id
                          ? {
                              first_name: prov.first_name,
                              last_name: prov.last_name,
                              id: prov.id,
                              profile_image: prov.profile_image,
                            }
                          : { id: null, first_name: 'Auto Match' };
                        dispatch(
                          setOrderDataAction({
                            orderProvider: provInfo,
                            orderTimeSlots: {
                              ...(orderType === 'oneTime'
                                ? { pickup_date: currentDate }
                                : { first_pickup_date: currentDate }),
                              pickup_start_time: t.default.start_time,
                              pickup_end_time: t.default.end_time,
                              timeZone: t.time_zone,
                            },
                          })
                        );
                      }}
                    >
                      {`${t.start_time} - ${t.end_time}`}
                    </button>
                  );
                })}
            </div>
          </div>
        )}
      </div>
    );
  };

  return (
    <div>
      {providersLoading ? (
        <Loader classes='h-32 w-full' bg text='Searching providers...' />
      ) : (
        <div className='space-y-2'>
          {providers.length > 0 && (
            <p className='text-client-300 text-xs font-semibold'>Best Matches In Your Neighborhood</p>
          )}
          {providers.map((p) => renderProviderCard(p))}
          {renderProviderCard({}, true)}
        </div>
      )}
      {/* Continue button */}
      <div className='mt-4 flex justify-end'>
        <button
          type='button'
          className={`rounded-full px-6 py-2 text-sm font-semibold text-white transition ${
            !hasValid ? 'cursor-not-allowed bg-slate-300' : 'bg-client-300 hover:bg-client-400 shadow'
          }`}
          onClick={onContinue}
          disabled={!hasValid}
        >
          Continue
        </button>
      </div>
    </div>
  );
}
